import React, { useState, forwardRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col, Modal } from "antd";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import SwitchItem from "../../../components/forms/SwitchItem";
import { MetaDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import { activateRecord, deactivateRecord } from "../employeeDetailsSlice";
import DetailsHeader from "../../../components/forms/DetailsHeader";
import { setIsPro } from "../employeeDetailsSlice";
import { useDispatch } from "react-redux";
import ImageFormItem from "../../../components/forms/ImageFormItem";
import RadioButtonItem from "../../../components/forms/RadioButtonItem";
import { FormattedMessage } from "react-intl";
import Surface from "../../../components/layouts/Surface";
import Spacer from "../../../components/layouts/Spacer";
import ResponsiveItemsWrapper from "../../../components/layouts/ResponsiveItemsWrapper";
import { activationConfirmMessage } from "../../../components/forms/formsFeedback";
import { useProfessionalRoles } from "../../../hooks";
import { useSelector } from "react-redux";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";

const PersonalDetails = forwardRef((props, ref) => {
  const { activeEmployee } = props;
  const intl = useIntl();
  let { branchId } = useParams();
  const [modal, contextHolder] = Modal.useModal();

  const [isProfessional, setIsProfessional] = useState(false);
  const professionalRoles = useProfessionalRoles();
  const dispatch = useDispatch();
  const changedValues = useSelector(
    (state) => state.employeeDetails.changedItems.values
  );
  useEffect(() => {
    if (professionalRoles && professionalRoles.length > 0) {
      const professional_role_id = changedValues.professional_role_id
        ? changedValues.professional_role_id
        : activeEmployee.professional_role_id;
      const filtered = professionalRoles.filter(
        (item) => item.id === professional_role_id
      )[0];
      const isPro = filtered && isProfessional;
      dispatch(setIsPro(isPro));
    }
  }, [
    professionalRoles,
    activeEmployee,
    changedValues,
    isProfessional,
    dispatch,
  ]);

  useEffect(() => {
    setIsProfessional(activeEmployee.professional_role_id !== null);
  }, [activeEmployee]);

  const onChange = (e) => {
    setIsProfessional(e.target.value);
  };

  const onActivate = (checked) => {
    activationConfirmMessage(
      activeEmployee,
      intl,
      checked,
      activeEmployee.surename + " " + activeEmployee.first_name,
      "employees",
      onActivateConfirmed,
      modal
    );
  };

  const onActivateConfirmed = (record) => {
    const params = {
      employeeId: record.id,
      branchId: branchId,
    };
    const toDispatch = record.active ? deactivateRecord : activateRecord;
    return dispatch(toDispatch(params));
  };

  const disabledDate = (current) => {
    const startdate = moment().subtract(16, "years");
    return current && current > startdate;
  };

  const defaultPickerValue = moment().subtract(16, "years");
  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      {contextHolder}
      <Surface>
        <DetailsHeader
          resource={"employees"}
          action={"editor"}
          editTitle={<FormattedMessage id="app.employees.personalDetails" />}
          hasSwitch={true}
          mode={"edit"}
          hideStatus={!activeEmployee.id}
          isActive={activeEmployee ? activeEmployee.active : false}
          onActivate={branchId && activeEmployee.id ? onActivate : null}
        />

        <Row>
          <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={8}>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  required={true}
                  name="surename"
                  category="employee"
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <InputItem
                  required={true}
                  name="first_name"
                  category="employee"
                />
              </div>
            </ResponsiveItemsWrapper>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem name="id" category="employee" disabled={true} />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  name="accounting_info.department_id"
                  category="employee"
                  metadataName={"departments"}
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  required={true}
                  name="local_id_number"
                  id={true}
                  category="employee"
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <DatePickerItem
                  required={true}
                  name="birthday"
                  category="employee"
                  disabledDate={disabledDate}
                  defaultPickerValue={defaultPickerValue}
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  name="accounting_info.number"
                  number={true}
                  category="employee"
                />
              </div>

              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <RadioButtonItem
                  name="is_professional_employee"
                  category="employee"
                  trueFalse={true}
                  onChange={onChange}
                />
              </div>
            </ResponsiveItemsWrapper>
            {isProfessional ? (
              <ResponsiveItemsWrapper>
                <div style={{ flex: 1 }}>
                  <MetaDataSelect
                    name="professional_role_id"
                    required={true}
                    category="employee"
                    metadataName={"professional_roles"}
                  />
                </div>

                <Spacer horizontal={true} />
                <div style={{ flex: 1 }}>
                  <InputItem
                    required={true}
                    number={true}
                    min={0}
                    max={100}
                    name="professional_role_position_percentage"
                    category="employee"
                  />
                </div>
              </ResponsiveItemsWrapper>
            ) : null}
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="care_giver"
                  category="employee"
                  itemRules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const iSstaffMember =
                          changedValues.staff_member !== undefined
                            ? changedValues.staff_member
                            : activeEmployee.staff_member;
                        if (value || iSstaffMember) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            intl.formatMessage({
                              id: "app.forms.employee.staff_or_worker",
                            })
                          )
                        );
                      },
                    }),
                  ]}
                />
              </div>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="staff_member"
                  category="employee"
                  itemRules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const iSworker =
                          changedValues.care_giver !== undefined
                            ? changedValues.care_giver
                            : activeEmployee.care_giver;
                        if (value || iSworker) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            intl.formatMessage({
                              id: "app.forms.employee.staff_or_worker",
                            })
                          )
                        );
                      },
                    }),
                  ]}
                />
              </div>
            </ResponsiveItemsWrapper>
          </Col>

          <Col
            xs={24}
            sm={24}
            md={24}
            lg={12}
            xl={{ span: 6, offset: 2 }}
            xxl={{ span: 4, offset: 2 }}
          >
            <ImageFormItem name="avatar_url" />
            <div style={{ height: "13px", width: "1px" }} />

            <RadioButtonItem
              required={true}
              name="gender_id"
              category="employee"
              genders={true}
              translated={true}
            />

            <MetaDataSelect
              required={true}
              name="family_status"
              category="employee"
              metadataName={"family_status"}
            />
            <MetaDataSelect
              required={true}
              name="employee_type_id"
              category="employee"
              metadataName={"employee_types"}
            />
          </Col>
        </Row>
      </Surface>
    </div>
  );
});

PersonalDetails.propTypes = {
  addImage: PropTypes.func,
  uri: PropTypes.string,
  activeEmployee: PropTypes.object,
};

export default PersonalDetails;
